import React, { useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { CheckCircle, XCircle, Search, RefreshCw } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  getAllVerificationOrders,
  completeVerificationOrder,
} from "services/verification.service";
import Modal from "Common/Components/Modal";
import { useFocusedRow } from "hooks/useFocusedRecord";
import {
  FocusNotFound,
  focusRowClass,
  scrollRowIntoView,
} from "Common/FocusedRow";

const STATUS_COLORS: Record<string, string> = {
  paid: "bg-blue-100 text-blue-800",
  in_progress: "bg-yellow-100 text-yellow-800",
  pending_payment: "bg-gray-100 text-gray-600",
  completed: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
};

const VerificationOrders = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [notes, setNotes] = useState("");

  const { data, isLoading, refetch } = useQuery(
    ["admin-verification-orders", statusFilter],
    () => getAllVerificationOrders(statusFilter || undefined)
  );

  const { isLoading: completing, mutate: complete } = useMutation(
    async ({ orderId, passed }: { orderId: string; passed: boolean }) =>
      completeVerificationOrder(orderId, passed, notes ? { notes } : {}),
    {
      onSuccess(_, { passed }) {
        toast.success(passed ? "Order marked as passed" : "Order marked as failed");
        queryClient.invalidateQueries(["admin-verification-orders"]);
        setShowCompleteModal(false);
        setNotes("");
        setSelectedOrder(null);
      },
      onError(error: any) {
        toast.error(error?.response?.data?.message || "Action failed");
      },
    }
  );

  const orders: any[] = data?.data ?? [];

  // Followed here from a verification alert. Completing an order is a modal
  // form, so this rings the row rather than opening it — the operator decides
  // when to act.
  const { notFound: focusNotFound, dismissFocus, isFocused } = useFocusedRow(
    orders,
    isLoading
  );

  const openComplete = (order: any) => {
    setSelectedOrder(order);
    setShowCompleteModal(true);
  };

  const workerName = (order: any) => {
    const u = order.targetUser;
    return u ? `${u.firstName} ${u.lastName}` : "—";
  };

  const requesterName = (order: any) => {
    const u = order.requestedBy;
    return u ? `${u.firstName} ${u.lastName}` : "—";
  };

  const serviceTypeName = (order: any) =>
    typeof order.serviceType === "object"
      ? order.serviceType?.name
      : order.serviceType;

  return (
    <React.Fragment>
      <BreadCrumb title="Verification Orders" pageTitle="Verification Orders" />
      <ToastContainer closeButton={false} limit={1} />

      {focusNotFound && <FocusNotFound onDismiss={dismissFocus} />}

      <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12">
        <div className="xl:col-span-12">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="flex flex-wrap gap-3 items-center justify-between p-4 border-b border-gray-100">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-48 text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Statuses</option>
                <option value="pending_payment">Pending Payment</option>
                <option value="paid">Paid</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </select>
              <button
                onClick={() => refetch()}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
              >
                <RefreshCw className="size-4" /> Refresh
              </button>
            </div>

            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="py-12 text-center text-gray-400">Loading...</div>
              ) : orders.length === 0 ? (
                <div className="py-12 text-center">
                  <Search className="size-6 mx-auto text-sky-500 fill-sky-100 mb-2" />
                  <p className="text-slate-500">No orders found</p>
                </div>
              ) : (
                <table data-stack className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-3.5 py-2.5 border border-gray-200 text-left font-semibold">Worker</th>
                      <th className="px-3.5 py-2.5 border border-gray-200 text-left font-semibold">Requested By</th>
                      <th className="px-3.5 py-2.5 border border-gray-200 text-left font-semibold">Service</th>
                      <th className="px-3.5 py-2.5 border border-gray-200 text-left font-semibold">Amount</th>
                      <th className="px-3.5 py-2.5 border border-gray-200 text-left font-semibold">Status</th>
                      <th className="px-3.5 py-2.5 border border-gray-200 text-left font-semibold">Result</th>
                      <th className="px-3.5 py-2.5 border border-gray-200 text-left font-semibold">Date</th>
                      <th className="px-3.5 py-2.5 border border-gray-200 text-left font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr
                        key={order._id}
                        ref={scrollRowIntoView(isFocused(order._id))}
                        className={focusRowClass(isFocused(order._id))}
                      >
                        <td data-label="Worker" data-cell="title" className="px-3.5 py-2.5 border border-gray-200">{workerName(order)}</td>
                        <td data-label="Requested By" className="px-3.5 py-2.5 border border-gray-200">{requesterName(order)}</td>
                        <td data-label="Service" className="px-3.5 py-2.5 border border-gray-200">{serviceTypeName(order)}</td>
                        <td data-label="Amount" className="px-3.5 py-2.5 border border-gray-200">
                          ₦{((order.payment?.amount ?? 0) / 100).toLocaleString()}
                        </td>
                        <td data-label="Status" className="px-3.5 py-2.5 border border-gray-200">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[order.status] ?? "bg-gray-100 text-gray-600"}`}>
                            {order.status}
                          </span>
                        </td>
                        <td data-label="Result" className="px-3.5 py-2.5 border border-gray-200">
                          {order.status === "completed" ? (
                            order.result?.passed ? (
                              <span className="text-green-600 flex items-center gap-1">
                                <CheckCircle className="size-4" /> Passed
                              </span>
                            ) : (
                              <span className="text-red-500 flex items-center gap-1">
                                <XCircle className="size-4" /> Failed
                              </span>
                            )
                          ) : "—"}
                        </td>
                        <td data-label="Date" className="px-3.5 py-2.5 border border-gray-200">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </td>
                        <td data-label="Action" data-cell="actions" className="px-3.5 py-2.5 border border-gray-200">
                          {["paid", "in_progress"].includes(order.status) && (
                            <button
                              onClick={() => openComplete(order)}
                              className="text-xs px-2 py-1 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                            >
                              Complete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={showCompleteModal}
        onHide={() => { setShowCompleteModal(false); setNotes(""); }}
        id="completeOrderModal"
        modal-center="true"
        size="md"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b border-slate-200"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500"
        >
          <Modal.Title className="text-16">Complete Verification Order</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 flex flex-col gap-4">
          {selectedOrder && (
            <div className="text-sm text-gray-600 space-y-1">
              <p><span className="font-medium">Worker:</span> {workerName(selectedOrder)}</p>
              <p><span className="font-medium">Service:</span> {serviceTypeName(selectedOrder)}</p>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              placeholder="Add notes about this verification..."
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => complete({ orderId: selectedOrder._id, passed: true })}
              disabled={completing}
              className="flex-1 py-2 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            >
              {completing ? "Saving..." : "Mark Passed"}
            </button>
            <button
              onClick={() => complete({ orderId: selectedOrder._id, passed: false })}
              disabled={completing}
              className="flex-1 py-2 bg-red-500 text-white rounded text-sm font-medium hover:bg-red-600 disabled:opacity-50"
            >
              Mark Failed
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default VerificationOrders;
